import { useState } from "react";
import { Plus, Pencil, Archive, ArchiveRestore, SlidersHorizontal, Package, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ProductModal from "@/components/inventory/ProductModal";
import StockAdjustModal from "@/components/inventory/StockAdjustModal";
import { INVENTORY } from "@/constants/testIds";
import { PRODUCT_CATEGORY_LABELS, formatQty } from "@/lib/inventoryConstants";

export default function ProductsTab({ products, loading, onCreate, onUpdate, onAdjust, onDeactivate, onReactivate }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const visibleProducts = products.filter((p) => (showArchived ? true : p.is_active));

  const handleSubmit = async (form, existing) => {
    if (existing) await onUpdate(existing.id, form);
    else await onCreate(form);
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    await onDeactivate(deactivateTarget.id);
    setDeactivateTarget(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer" data-testid={INVENTORY.productsShowArchivedToggle}>
          <Switch checked={showArchived} onCheckedChange={setShowArchived} />
          Ver archivados
        </label>
        <Button className="bg-brand hover:bg-brand-hover text-white" onClick={() => { setEditing(null); setModalOpen(true); }} data-testid={INVENTORY.productsAddButton}>
          <Plus className="w-4 h-4" /> Nuevo producto
        </Button>
      </div>

      {loading ? (
        <p className="text-center text-muted-foreground py-12">Cargando productos...</p>
      ) : visibleProducts.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-12 text-center" data-testid={INVENTORY.productsEmptyState}>
          <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">{showArchived ? "No hay productos archivados." : "Aun no cargaste productos en el inventario."}</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-x-auto">
          <Table data-testid={INVENTORY.productsTable}>
            <TableHeader>
              <TableRow>
                <TableHead>Producto</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Costo unitario</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleProducts.map((p) => (
                <TableRow key={p.id} className={!p.is_active ? "opacity-60" : ""} data-testid={`${INVENTORY.productRowPrefix}${p.id}`}>
                  <TableCell>
                    <p className="font-medium text-foreground">{p.name}{!p.is_active && <span className="ml-2 text-xs text-muted-foreground">(archivado)</span>}</p>
                    <p className="text-xs text-muted-foreground">{p.brand}</p>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{PRODUCT_CATEGORY_LABELS[p.category] || p.category}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{formatQty(p.stock_quantity)} {p.unit}</span>
                      {p.low_stock && (
                        <Badge className="bg-[#F4E1E1] text-[#A34B49] border-none gap-1 font-normal text-[11px]" data-testid={`${INVENTORY.productLowStockBadgePrefix}${p.id}`}>
                          <AlertTriangle className="w-3 h-3" /> Stock bajo
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">min: {formatQty(p.min_stock)} {p.unit}</p>
                  </TableCell>
                  <TableCell className="text-sm">{p.unit_cost != null ? `$${p.unit_cost.toFixed(2)}` : "\u2014"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setAdjustTarget(p)} data-testid={`${INVENTORY.productAdjustButtonPrefix}${p.id}`} title="Ajustar stock">
                        <SlidersHorizontal className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(p); setModalOpen(true); }} data-testid={`${INVENTORY.productEditButtonPrefix}${p.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {p.is_active ? (
                        <Button variant="ghost" size="icon" onClick={() => setDeactivateTarget(p)} data-testid={`${INVENTORY.productDeactivateButtonPrefix}${p.id}`}>
                          <Archive className="w-4 h-4 text-destructive" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" onClick={() => onReactivate(p.id)} data-testid={`${INVENTORY.productReactivateButtonPrefix}${p.id}`} title="Reactivar">
                          <ArchiveRestore className="w-4 h-4 text-brand" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ProductModal open={modalOpen} onOpenChange={setModalOpen} product={editing} onSubmit={handleSubmit} />
      <StockAdjustModal open={Boolean(adjustTarget)} onOpenChange={(o) => !o && setAdjustTarget(null)} product={adjustTarget} onSubmit={(payload) => onAdjust(adjustTarget.id, payload)} />

      <AlertDialog open={Boolean(deactivateTarget)} onOpenChange={(o) => !o && setDeactivateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar {deactivateTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              El producto dejara de estar disponible para compras y tratamientos, pero se conserva su historial. Podes reactivarlo despues desde "Ver archivados".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate} className="bg-destructive hover:bg-destructive/90">Desactivar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
